import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import Modal from '../../components/ui/Modal';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Edit2, Trash2, Shield, User, Mail, Lock, UserPlus, Info, ShieldOff, Eye, MessageCircle, KeyRound, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const ALL_ROLES = [
  { value: 'support', label: 'Support Agent' },
  { value: 'moderator', label: 'Finance / Moderator' },
  { value: 'staff', label: 'Staff (Referral)' },
  { value: 'admin', label: 'Admin (Full access)', masterOnly: true },
];

// Menu → required permission map (mirrors AdminLayout.jsx)
// Used to compute the live preview inside the edit modal.
const MENU_PREVIEW = [
  { label: 'Dashboard', perm: null },
  { label: 'Players', perm: 'users:view' },
  { label: 'Transactions', perm: 'transactions:view' },
  { label: 'Deposit Requests', perm: 'payments:view' },
  { label: 'Withdrawal Requests', perm: 'payments:view' },
  { label: 'Games', perm: 'system:config' },
  { label: 'Game Providers', perm: 'system:config' },
  { label: 'Payments Configuration', perm: 'system:config' },
  { label: 'Tasks & Rewards', perm: 'promotions:edit' },
  { label: 'VIP Tiers', perm: 'system:config' },
  { label: 'Announcements', perm: 'announcements:manage' },
  { label: 'Staff', perm: 'staff:manage' },
  { label: 'Live Chat', perm: 'chat:respond' },
  { label: 'Notifications', perm: 'notifications:broadcast' },
  { label: 'Telegram Bots', perm: 'system:config' },
  { label: 'Game Records', perm: 'analytics:view' },
  { label: 'Activity Analytics', perm: 'analytics:view' },
  { label: 'Staff Activity', perm: 'audit:view' },
  { label: 'Live Activity', perm: 'analytics:view' },
  { label: 'Segmentation', perm: 'analytics:view' },
  { label: 'Settings', perm: 'system:config' },
];

export default function StaffPage() {
  const { user: adminUser } = useAuth();
  const [staff, setStaff] = useState([]);
  const [catalog, setCatalog] = useState([]); // [{key, name, description, category, risk_level}]
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [chatStaffForm, setChatStaffForm] = useState({
    email: '',
    username: '',
    full_name: '',
    password: '',
  });
  const [chatStaffSaving, setChatStaffSaving] = useState(false);

  const isMaster = adminUser?.is_master_admin;
  const availableRoles = ALL_ROLES.filter(r => !r.masterOnly || isMaster);

  const [formData, setFormData] = useState({
    username: '', email: '', full_name: '', password: '',
    role: 'support', permissions: [],
  });

  // Phase 18: bulk selection
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    const eligible = staff.filter(m => !m.is_master_admin && (m.permissions || []).length > 0).map(m => m.user_id);
    setSelectedIds(new Set(eligible));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkRevokeAll = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Revoke ALL permissions from ${selectedIds.size} staff member(s)? They'll keep login but be blocked from every admin page until perms are granted again.`)) return;
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map(id => api.put(`/api/admin/staff/${id}`, { permissions: [] }))
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      toast.success(`${ok} revoked${failed ? `, ${failed} failed` : ''}`);
      clearSelection();
      fetchStaff();
    } catch {
      toast.error('Bulk revoke failed');
    }
  };

  const liveChatStaff = staff.filter(m => (m.permissions || []).includes('chat:respond'));

  const createLiveChatStaff = async (e) => {
    e.preventDefault();
    if (!chatStaffForm.email.trim() || !chatStaffForm.password.trim()) {
      toast.error('Staff ID/email and password are required');
      return;
    }
    if (chatStaffForm.password.length < 6) {
      toast.error('Use at least 6 characters for staff password');
      return;
    }
    setChatStaffSaving(true);
    try {
      const email = chatStaffForm.email.trim();
      const username = (chatStaffForm.username || email.split('@')[0]).trim();
      await api.post('/api/admin/staff', {
        email,
        username,
        full_name: chatStaffForm.full_name || username,
        password: chatStaffForm.password,
        role: 'support',
        permissions: ['chat:respond'],
      });
      toast.success('Live chat staff login created');
      setChatStaffForm({ email: '', username: '', full_name: '', password: '' });
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create live chat staff');
    } finally {
      setChatStaffSaving(false);
    }
  };

  const setLiveChatAccess = async (member, enabled) => {
    const current = new Set(member.permissions || []);
    if (enabled) current.add('chat:respond');
    else current.delete('chat:respond');
    try {
      await api.put(`/api/admin/staff/${member.user_id}`, { permissions: Array.from(current) });
      toast.success(enabled ? 'Live chat access granted' : 'Live chat access removed');
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update live chat access');
    }
  };

  useEffect(() => {
    fetchStaff();
    fetchCatalog();
  }, []);

  const fetchCatalog = async () => {
    try {
      const res = await api.get('/api/admin/staff/permissions/catalog');
      setCatalog(res.data.catalog || []);
    } catch { /* empty */ }
  };

  const fetchStaff = async () => {
    try {
      const res = await api.get('/api/admin/staff');
      setStaff(res.data.staff || []);
    } catch (err) { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = {
          full_name: formData.full_name,
          role: formData.role,
          permissions: formData.permissions,
        };
        if (formData.password) payload.new_password = formData.password;
        await api.put(`/api/admin/staff/${editing.user_id}`, payload);
        toast.success('Staff updated');
      } else {
        if (!formData.email || !formData.password) {
          toast.error('Email and password are required');
          setSubmitting(false);
          return;
        }
        await api.post('/api/admin/staff', {
          email: formData.email,
          username: formData.username || undefined,
          full_name: formData.full_name,
          password: formData.password,
          role: formData.role,
          permissions: formData.permissions,
        });
        toast.success('Staff member created!');
      }
      setShowForm(false);
      setEditing(null);
      resetForm();
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await api.delete(`/api/admin/staff/${userId}`);
      toast.success('Staff deleted');
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleSuspend = async (member) => {
    try {
      await api.put(`/api/admin/staff/${member.user_id}`, { disabled: !member.is_suspended });
      toast.success(member.is_suspended ? 'Staff enabled' : 'Staff suspended');
      fetchStaff();
    } catch { toast.error('Failed'); }
  };

  const revokeAllPermissions = async (member) => {
    if (!window.confirm(`Revoke ALL permissions from ${member.full_name || member.username}? They will keep their login but cannot access any admin page until you grant permissions again.`)) return;
    try {
      await api.put(`/api/admin/staff/${member.user_id}`, { permissions: [] });
      toast.success(`All permissions revoked from ${member.username}`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke permissions');
    }
  };

  const togglePermission = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const resetForm = () => {
    setFormData({ username: '', email: '', full_name: '', password: '', role: 'support', permissions: [] });
  };

  const openEdit = (member) => {
    setEditing(member);
    setFormData({
      username: member.username || '', email: member.email || '', full_name: member.full_name || '',
      password: '', role: member.role || 'support', permissions: member.permissions || [],
    });
    setShowForm(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Staff Management</h2>
          <p className="text-sm text-gray-400">{staff.length} staff members</p>
        </div>
        <Button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} className="bg-[#D4AF37] text-black" data-testid="add-staff-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add Staff
        </Button>
      </div>

      <div
        className="rounded-2xl border border-blue-500/25 bg-blue-500/5 p-4"
        data-testid="live-chat-staff-access"
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/15 text-blue-300">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Live Chat Staff Access</h3>
                <p className="text-xs text-gray-400">
                  Create staff ID/password for the APK or admin live chat. This grants only Live Chat access.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {liveChatStaff.length > 0 ? liveChatStaff.slice(0, 4).map(m => (
                <span key={m.user_id} className="rounded-full bg-blue-500/10 px-2 py-1 text-[10px] text-blue-200">
                  {m.email}
                </span>
              )) : (
                <span className="rounded-full bg-orange-500/10 px-2 py-1 text-[10px] text-orange-300">
                  No live chat staff yet
                </span>
              )}
              {liveChatStaff.length > 4 && <span className="text-[10px] text-gray-500">+{liveChatStaff.length - 4} more</span>}
            </div>
          </div>
          <a
            href={`${window.location.pathname.replace(/\/staff$/, '')}/chat`}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-gray-200 hover:bg-white/5"
          >
            Configure chats <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <form onSubmit={createLiveChatStaff} className="mt-4 grid gap-2 md:grid-cols-4">
          <div>
            <label className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500">
              <Mail className="h-3 w-3" /> Staff ID / Email
            </label>
            <Input
              type="email"
              value={chatStaffForm.email}
              onChange={e => setChatStaffForm(p => ({ ...p, email: e.target.value }))}
              placeholder="support@starlight.local"
              className="h-10 bg-[#0B0E14] border-white/10"
              data-testid="live-chat-staff-email"
            />
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500">
              <User className="h-3 w-3" /> Name
            </label>
            <Input
              value={chatStaffForm.full_name}
              onChange={e => setChatStaffForm(p => ({ ...p, full_name: e.target.value }))}
              placeholder="Support Staff"
              className="h-10 bg-[#0B0E14] border-white/10"
              data-testid="live-chat-staff-name"
            />
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500">
              <KeyRound className="h-3 w-3" /> Password
            </label>
            <Input
              type="password"
              value={chatStaffForm.password}
              onChange={e => setChatStaffForm(p => ({ ...p, password: e.target.value }))}
              placeholder="Set login password"
              className="h-10 bg-[#0B0E14] border-white/10"
              data-testid="live-chat-staff-password"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={chatStaffSaving}
              className="h-10 w-full bg-blue-500 text-white hover:bg-blue-400"
              data-testid="create-live-chat-staff"
            >
              {chatStaffSaving ? 'Creating...' : 'Create Chat Login'}
            </Button>
          </div>
        </form>
      </div>

      {/* Phase 17 Policy notice — Permissions Catalog is the single source of truth */}
      <div
        data-testid="rbac-policy-banner"
        className="flex items-start gap-3 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3.5"
      >
        <Info className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-[#D4AF37]">Permissions Catalog is the single source of truth</p>
          <p className="text-xs text-gray-300 leading-relaxed">
            Role is just a label. A staff member can only access what you explicitly grant them in the
            Permissions list below — granting or revoking any permission takes effect <span className="text-[#D4AF37] font-semibold">instantly</span>.
            Use <span className="font-semibold">Revoke All</span> to quickly disable access without deleting the account.
          </p>
        </div>
      </div>

      {/* Phase 18: Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="flex items-center justify-between rounded-lg bg-orange-500/10 border border-orange-500/30 px-4 py-2.5 animate-fade-in-up"
        >
          <div className="flex items-center gap-3">
            <ShieldOff className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-medium text-orange-300">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              onClick={selectAllVisible}
              className="text-xs text-gray-400 hover:text-gray-200"
            >
              Select all with perms
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-gray-400 hover:text-gray-200"
            >
              Clear
            </button>
          </div>
          <Button
            size="sm"
            onClick={bulkRevokeAll}
            className="bg-orange-500 hover:bg-orange-400 text-black font-medium"
            data-testid="bulk-revoke-all-btn"
          >
            <ShieldOff className="w-3.5 h-3.5 mr-1.5" /> Revoke all permissions ({selectedIds.size})
          </Button>
        </div>
      )}

      {/* Staff Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="staff-table">
          <thead>
            <tr className="border-b border-white/10 text-gray-400 text-xs">
              <th className="text-left py-3 px-4 w-8"></th>
              <th className="text-left py-3 px-4">Staff</th>
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-left py-3 px-4">Role</th>
              <th className="text-left py-3 px-4">Referral Code</th>
              <th className="text-left py-3 px-4">Permissions</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map(m => (
              <tr key={m.user_id} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-3 px-4">
                  {!m.is_master_admin && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(m.user_id)}
                      onChange={() => toggleSelect(m.user_id)}
                      className="w-4 h-4 rounded accent-[#D4AF37] cursor-pointer"
                      data-testid={`select-staff-${m.user_id}`}
                    />
                  )}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">
                      {(m.full_name || m.username || 'S').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{m.full_name || m.username}</p>
                      <p className="text-[10px] text-gray-500">@{m.username}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4 text-gray-400">{m.email}</td>
                <td className="py-3 px-4">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 capitalize">{m.role}</span>
                  {m.is_master_admin && <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37]">MASTER</span>}
                </td>
                <td className="py-3 px-4 text-xs text-gray-400 font-mono">{m.referral_code || '-'}</td>
                <td className="py-3 px-4">
                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                    {(m.permissions || []).slice(0, 2).map(p => (
                      <span key={p} className="text-[9px] bg-white/5 px-1.5 py-0.5 rounded">{p.replace(/_/g, ' ')}</span>
                    ))}
                    {(m.permissions || []).length > 2 && <span className="text-[9px] text-gray-500">+{m.permissions.length - 2}</span>}
                    {(m.permissions || []).length === 0 && <span className="text-[9px] text-red-400">None — no access</span>}
                  </div>
                  {!m.is_master_admin && (
                    <button
                      type="button"
                      onClick={() => setLiveChatAccess(m, !(m.permissions || []).includes('chat:respond'))}
                      className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] transition ${
                        (m.permissions || []).includes('chat:respond')
                          ? 'bg-blue-500/15 text-blue-300 hover:bg-blue-500/25'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'
                      }`}
                      data-testid={`toggle-live-chat-${m.user_id}`}
                    >
                      <MessageCircle className="h-3 w-3" />
                      {(m.permissions || []).includes('chat:respond') ? 'Live chat on' : 'Grant chat'}
                    </button>
                  )}
                </td>
                <td className="py-3 px-4">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.is_suspended ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                    {m.is_suspended ? 'Suspended' : 'Active'}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(m)} className="text-xs h-7" data-testid={`edit-staff-${m.user_id}`}><Edit2 className="w-3 h-3" /></Button>
                    {!m.is_master_admin && (m.permissions || []).length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revokeAllPermissions(m)}
                        className="text-xs h-7 text-orange-400 hover:text-orange-300"
                        title="Revoke all permissions (keeps account, blocks admin access)"
                        data-testid={`revoke-all-${m.user_id}`}
                      >
                        <ShieldOff className="w-3 h-3" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => toggleSuspend(m)} className="text-xs h-7">{m.is_suspended ? 'Enable' : 'Suspend'}</Button>
                    {!m.is_master_admin && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(m.user_id)} className="text-xs h-7 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {staff.length === 0 && (
              <tr><td colSpan="8" className="text-center py-8 text-gray-500">No staff members yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal - ALL FIELDS VISIBLE */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Staff Member' : 'Add New Staff Member'} maxWidth="max-w-lg">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block flex items-center gap-1"><User className="w-3 h-3" /> Username</label>
                <Input
                  value={formData.username}
                  onChange={e => setFormData(p => ({ ...p, username: e.target.value }))}
                  placeholder="e.g. johndoe"
                  className="bg-[#0B0E14] border-white/10"
                  disabled={!!editing}
                  data-testid="staff-username"
                />
              </div>

              {/* Full Name */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block flex items-center gap-1"><User className="w-3 h-3" /> Full Name</label>
                <Input
                  value={formData.full_name}
                  onChange={e => setFormData(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="e.g. John Doe"
                  className="bg-[#0B0E14] border-white/10"
                  data-testid="staff-fullname"
                />
              </div>

              {/* Email */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="e.g. john@teamlobby.local"
                  className="bg-[#0B0E14] border-white/10"
                  required
                  disabled={!!editing}
                  data-testid="staff-email"
                />
              </div>

              {/* Password (create only) */}
              {!editing && (
                <div>
                  <label className="text-xs text-gray-400 mb-1 block flex items-center gap-1"><Lock className="w-3 h-3" /> Password</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                    placeholder="Minimum 8 characters"
                    className="bg-[#0B0E14] border-white/10"
                    required
                    data-testid="staff-password"
                  />
                </div>
              )}

              {/* Reset Password (edit mode) */}
              {editing && (
                <div>
                  <label className="text-xs text-gray-400 mb-1 block flex items-center gap-1"><Lock className="w-3 h-3" /> New Password (leave empty to keep current)</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                    placeholder="Enter new password to reset"
                    className="bg-[#0B0E14] border-white/10"
                    data-testid="staff-new-password"
                  />
                </div>
              )}

              {/* Role */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Role</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}
                  className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white"
                  data-testid="staff-role"
                >
                  {availableRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                {!isMaster && (
                  <p className="text-[10px] text-gray-500 mt-1">Admin role restricted to master admin.</p>
                )}
              </div>

              {/* Permissions — grouped by category */}
              <div>
                <label className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Permissions
                  <span className="ml-auto text-[10px] text-gray-600">{formData.permissions.length} selected</span>
                </label>
                {/* Single source of truth reminder */}
                <div className="flex items-center justify-between gap-2 mb-2 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/5">
                  <p className="text-[10px] text-gray-400">
                    Only checked permissions grant access. Role is a label.
                  </p>
                  {formData.permissions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData(p => ({ ...p, permissions: [] }))}
                      className="text-[10px] text-orange-400 hover:text-orange-300 flex items-center gap-1 font-medium"
                      data-testid="modal-revoke-all-perms"
                    >
                      <ShieldOff className="w-3 h-3" /> Revoke all
                    </button>
                  )}
                </div>
                <div className="bg-[#0B0E14] rounded-lg p-3 space-y-3 max-h-80 overflow-y-auto">
                  {Object.entries(catalog.reduce((acc, p) => {
                    const cat = p.category || 'other';
                    (acc[cat] = acc[cat] || []).push(p);
                    return acc;
                  }, {})).map(([cat, perms]) => (
                    <div key={cat}>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 font-semibold">{cat}</p>
                      <div className="grid grid-cols-1 gap-1.5">
                        {perms.map(p => (
                          <label key={p.key} className="flex items-start gap-2 cursor-pointer text-xs py-1 hover:bg-white/5 px-1.5 rounded transition">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(p.key)}
                              onChange={() => togglePermission(p.key)}
                              className="w-3.5 h-3.5 rounded accent-[#D4AF37] mt-0.5"
                              data-testid={`perm-${p.key}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-gray-200 font-medium">{p.name}</span>
                                {p.risk_level === 'critical' && <span className="text-[8px] px-1 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">CRITICAL</span>}
                                {p.risk_level === 'high' && <span className="text-[8px] px-1 py-0.5 rounded bg-orange-500/20 text-orange-400 font-bold">HIGH</span>}
                              </div>
                              <p className="text-[10px] text-gray-500 leading-tight">{p.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  {catalog.length === 0 && <p className="text-xs text-gray-500 text-center py-4">Loading permissions…</p>}
                </div>
              </div>

              {/* Phase 18: Live permission preview — shows exactly what this staff will see */}
              <div data-testid="perm-preview">
                <label className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                  <Eye className="w-3 h-3" /> Preview — what this staff will see
                </label>
                <div className="bg-[#0B0E14] rounded-lg p-3 space-y-3">
                  {/* Menus visible */}
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 font-semibold">Admin menus ({MENU_PREVIEW.filter(i => !i.perm || formData.permissions.includes(i.perm)).length} / {MENU_PREVIEW.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {MENU_PREVIEW.map(item => {
                        const visible = !item.perm || formData.permissions.includes(item.perm);
                        return (
                          <span
                            key={item.label}
                            className={`text-[10px] px-2 py-0.5 rounded-full ${visible ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-white/5 text-gray-600 line-through'}`}
                            title={item.perm ? `Requires ${item.perm}` : 'Always visible'}
                          >
                            {item.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Capability summary */}
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    {[
                      { label: 'Can view player emails', perm: 'users:view_email', pii: true },
                      { label: 'Can approve payments', perm: 'payments:approve' },
                      { label: 'Can reject payments', perm: 'payments:reject' },
                      { label: 'Can adjust wallets', perm: 'users:wallet' },
                      { label: 'Can disable users', perm: 'users:disable' },
                      { label: 'Can manage staff', perm: 'staff:manage' },
                      { label: 'Can respond in chat', perm: 'chat:respond' },
                      { label: 'Can broadcast notifications', perm: 'notifications:broadcast' },
                    ].map(cap => {
                      const has = formData.permissions.includes(cap.perm);
                      return (
                        <div key={cap.perm} className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${has ? 'bg-green-400' : 'bg-gray-600'}`} />
                          <span className={has ? 'text-gray-200' : 'text-gray-500'}>{cap.label}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Player email preview banner */}
                  {!formData.permissions.includes('users:view_email') && (
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded px-2.5 py-1.5">
                      <Mail className="w-3 h-3" />
                      <span>Player emails will appear masked (e.g. <span className="font-mono">j****e@example.com</span>). Username remains visible.</span>
                    </div>
                  )}

                  {formData.permissions.length === 0 && (
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2.5 py-1.5">
                      <ShieldOff className="w-3 h-3" />
                      <span>This staff will have <span className="font-semibold">no access</span> — only the Dashboard placeholder and logout will work.</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2 border-t border-white/5">
                <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-white/10">Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-[#D4AF37] text-black" data-testid="staff-submit">
                  {submitting ? 'Saving...' : editing ? 'Update Staff' : 'Create Staff'}
                </Button>
              </div>
            </form>
      </Modal>
    </div>
  );
}
